import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, DollarSign, Globe, TrendingUp, TrendingDown,
  ArrowLeft, RefreshCw, Download, Search, FileText,
  BarChart2, AlertTriangle, Brain, ChevronRight, Zap, Clock, Grid
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { apiCall } from './Api';
import { useAuth } from './contexts/AuthContext';

// ─── Icon resolver + formatters (local, to keep the file self-contained) ────
const ICON_MAP = {
  Users, DollarSign, Globe, TrendingUp, TrendingDown,
  BarChart2, AlertTriangle, FileText, Zap, Clock, Grid
};
const resolveIcon = (name) => ICON_MAP[name] || FileText;

const formatNumber = (num) => {
  if (num == null) return '—';
  if (Math.abs(num) >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return Number.isInteger(num) ? num.toLocaleString() : num.toFixed(2);
};
const formatCurrency = (amount) => {
  if (amount == null) return '—';
  if (Math.abs(amount) >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
  if (Math.abs(amount) >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount.toFixed(2)}`;
};
const formatPct = (v, decimals = 1) => {
  if (v == null) return '—';
  return `${(v * 100).toFixed(decimals)}%`;
};
const formatDeltaPct = (v, decimals = 1) => {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(decimals)}%`;
};
const formatDurationMs = (ms) => {
  if (ms == null) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
};

// ─── Category config ──────────────────────────────────────────
const CATEGORIES = [
  { key: 'all',          label: 'All',          icon: Brain },
  { key: 'self_service', label: 'Self Service', icon: FileText },
  { key: 'commercial',   label: 'Commercial',   icon: Users },
  { key: 'operations',   label: 'Operations',   icon: Globe },
  { key: 'financial',    label: 'Financial',    icon: DollarSign },
  { key: 'executive',    label: 'Executive',    icon: TrendingUp },
];

// ═══════════════════════════════════════════════════════════════
// Column schemas for tabular reports
// ═══════════════════════════════════════════════════════════════
const CUSTOMER_HEALTH_COLUMNS = [
  { key: 'severity',                label: '',                   width: 'w-1' },
  { key: 'customer_name',           label: 'Customer',           sticky: true },
  { key: 'account_manager',         label: 'Account Manager' },
  { key: 'volume_current',          label: 'Volume (7d)',        format: 'number',     align: 'right' },
  { key: 'volume_delta_pct',        label: 'Vol Δ',              format: 'delta_pct',  align: 'right' },
  { key: 'delivery_rate_current',   label: 'Delivery',           format: 'pct',        align: 'right' },
  { key: 'delivery_rate_delta_pct', label: 'Delivery Δ',         format: 'delta_pct',  align: 'right' },
  { key: 'error_rate_current',      label: 'Error Rate',         format: 'pct',        align: 'right' },
  { key: 'error_rate_delta_pct',    label: 'Err Δ',              format: 'delta_pct',  align: 'right' },
  { key: 'avg_latency_ms_current',  label: 'Avg Latency',        format: 'duration',   align: 'right' },
  { key: 'margin_current',          label: 'Margin',             format: 'currency',   align: 'right' },
  { key: 'margin_delta_pct',        label: 'Margin Δ',           format: 'delta_pct',  align: 'right' },
  { key: 'volume_spark',            label: '13-wk volume',       format: 'spark',      align: 'center', width: 'w-24' },
];

const MARGIN_LEAKAGE_COLUMNS = [
  { key: 'customer_name',   label: 'Customer',     sticky: true },
  { key: 'vendor_name',     label: 'Vendor' },
  { key: 'country_name',    label: 'Country' },
  { key: 'volume',          label: 'Volume',       format: 'number',   align: 'right' },
  { key: 'revenue',         label: 'Revenue',      format: 'currency', align: 'right' },
  { key: 'cost',            label: 'Cost',         format: 'currency', align: 'right' },
  { key: 'margin',          label: 'Margin',       format: 'margin',   align: 'right' },
  { key: 'margin_pct',      label: 'Margin %',     format: 'margin_pct', align: 'right' },
  { key: 'margin_per_msg',  label: '$/Msg',        format: 'currency_precise', align: 'right' },
  { key: 'dollars_at_risk', label: '$ at Risk',    format: 'currency', align: 'right' },
];

const VENDOR_RELIABILITY_COLUMNS = [
  { key: 'severity',                    label: '',                     width: 'w-1' },
  { key: 'vendor_name',                 label: 'Vendor',               sticky: true },
  { key: 'account_manager',             label: 'Account Manager' },
  { key: 'volume_current',              label: 'Volume',               format: 'number',    align: 'right' },
  { key: 'volume_delta_pct',            label: 'Vol Δ',                format: 'delta_pct', align: 'right' },
  { key: 'delivery_rate_current',       label: 'Delivery',             format: 'pct',       align: 'right' },
  { key: 'delivery_rate_delta_pct',     label: 'Delivery Δ',           format: 'delta_pct', align: 'right' },
  { key: 'error_rate_current',          label: 'Error',                format: 'pct',       align: 'right' },
  { key: 'retry_rate_current',          label: 'Retries/Msg',          format: 'retry_rate', align: 'right' },
  { key: 'fake_dlr_rate_current',       label: 'Fake DLR',             format: 'pct',       align: 'right' },
  { key: 'platform_ingress_ms_current', label: 'Platform ms',          format: 'duration',  align: 'right' },
  { key: 'vendor_ack_ms_current',       label: 'ACK ms',               format: 'duration',  align: 'right' },
  { key: 'e2e_ms_current',              label: 'E2E ms',               format: 'duration',  align: 'right' },
  { key: 'delivered_only_ms_current',   label: 'Delivered ms',         format: 'duration',  align: 'right' },
];

const ERROR_CODE_DRIFT_COLUMNS = [
  { key: 'new_pill',           label: '',                 width: 'w-1' },
  { key: 'error_code',         label: 'Error Code',       sticky: true },
  { key: 'vendor_name',        label: 'Vendor' },
  { key: 'current_count',      label: 'Current Count',    format: 'number',     align: 'right' },
  { key: 'baseline_count',     label: 'Baseline Count',   format: 'number',     align: 'right' },
  { key: 'current_share_pct',  label: 'Current Share',    format: 'pct_raw',    align: 'right' },
  { key: 'baseline_share_pct', label: 'Baseline Share',   format: 'pct_raw',    align: 'right' },
  { key: 'drift_pp',           label: 'Drift (pp)',       format: 'drift_pp',   align: 'right' },
];

const CHURN_RADAR_COLUMNS = [
  { key: 'severity',               label: '',                    width: 'w-1' },
  { key: 'customer_name',          label: 'Customer',            sticky: true },
  { key: 'account_manager',        label: 'Account Manager' },
  { key: 'volume_current',         label: 'Volume',              format: 'number',    align: 'right' },
  { key: 'volume_baseline_scaled', label: 'Baseline (scaled)',   format: 'number',    align: 'right' },
  { key: 'volume_delta_pct',       label: 'Drop %',              format: 'delta_pct', align: 'right' },
  { key: 'delivery_rate_current',  label: 'Delivery',            format: 'pct',       align: 'right' },
  { key: 'delivery_rate_delta_pct',label: 'Delivery Δ',          format: 'delta_pct', align: 'right' },
  { key: 'revenue_current',        label: 'Revenue',             format: 'currency',  align: 'right' },
  { key: 'days_since_peak',        label: 'Days Since Peak',     format: 'number',    align: 'right' },
  { key: 'last_active_date',       label: 'Last Active',         align: 'right' },
];

const EXPANSION_NEW_COUNTRIES_COLUMNS = [
  { key: 'customer_name',    label: 'Customer',         sticky: true },
  { key: 'account_manager',  label: 'Account Manager' },
  { key: 'country_name',     label: 'Country' },
  { key: 'country_code',     label: 'Code' },
  { key: 'current_volume',   label: 'Volume',           format: 'number',   align: 'right' },
  { key: 'current_revenue',  label: 'Revenue',          format: 'currency', align: 'right' },
];

const EXPANSION_GROWTH_COLUMNS = [
  { key: 'customer_name',          label: 'Customer',            sticky: true },
  { key: 'account_manager',        label: 'Account Manager' },
  { key: 'volume_current',         label: 'Volume',              format: 'number',    align: 'right' },
  { key: 'volume_baseline_scaled', label: 'Baseline (scaled)',   format: 'number',    align: 'right' },
  { key: 'volume_delta_pct',       label: 'Volume Δ',            format: 'delta_pct', align: 'right' },
  { key: 'revenue_current',        label: 'Revenue',             format: 'currency',  align: 'right' },
  { key: 'revenue_delta_pct',      label: 'Revenue Δ',           format: 'delta_pct', align: 'right' },
  { key: 'delivery_rate_current',  label: 'Delivery',            format: 'pct',       align: 'right' },
];

const RETRY_STORM_COLUMNS = [
  { key: 'vendor_name',          label: 'Vendor',               sticky: true },
  { key: 'start_date',           label: 'Start' },
  { key: 'end_date',             label: 'End' },
  { key: 'duration_days',        label: 'Days',                 format: 'number',    align: 'right' },
  { key: 'peak_retry_rate',      label: 'Peak Rate',            format: 'pct_raw',   align: 'right' },
  { key: 'baseline_retry_rate',  label: 'Baseline',             format: 'pct_raw',   align: 'right' },
  { key: 'peak_multiplier',      label: 'Peak ×',               format: 'number',    align: 'right' },
  { key: 'peak_day',             label: 'Peak Day' },
  { key: 'total_retries',        label: 'Total Retries',        format: 'number',    align: 'right' },
  { key: 'avg_retry_rate',       label: 'Avg Rate',             format: 'pct_raw',   align: 'right' },
];

// ═══════════════════════════════════════════════════════════════
// Cell renderers
// ═══════════════════════════════════════════════════════════════
const renderSeverityPill = (severity) => {
  const colors = {
    red:    'bg-red-500',
    yellow: 'bg-yellow-500',
    green:  'bg-green-500',
    unknown: 'bg-gray-300',
  };
  return (
    <div className={`w-1 h-6 rounded-sm ${colors[severity] || colors.unknown}`} title={severity || 'unknown'} />
  );
};

// Drift in percentage-points: color = direction of change.
// Positive drift = this error's share is growing (bad signal).
const renderDriftPp = (v) => {
  if (v == null) return <span className="text-gray-400">—</span>;
  const sign = v > 0 ? '+' : '';
  const cls = v > 3 ? 'text-red-700 font-semibold'
            : v > 1 ? 'text-red-500'
            : v < -3 ? 'text-green-700 font-semibold'
            : v < -1 ? 'text-green-500'
            : 'text-gray-500';
  return <span className={cls}>{sign}{Number(v).toFixed(2)} pp</span>;
};

// "NEW" pill for first-seen error codes
const renderNewPill = (isNew) => {
  if (!isNew) return null;
  return (
    <div className="w-1 h-6 bg-orange-500 rounded-sm" title="New error code (not in baseline)" />
  );
};

const renderDeltaPct = (v) => {
  if (v == null) return <span className="text-gray-400">—</span>;
  const cls = v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : 'text-gray-500';
  const Icon = v > 0 ? TrendingUp : v < 0 ? TrendingDown : null;
  return (
    <span className={`inline-flex items-center space-x-0.5 font-medium ${cls}`}>
      {Icon && <Icon size={11} />}
      <span>{formatDeltaPct(v)}</span>
    </span>
  );
};

const renderMargin = (v) => {
  if (v == null) return <span className="text-gray-400">—</span>;
  const cls = v < 0 ? 'text-red-600 font-semibold' : 'text-gray-900';
  return <span className={cls}>{formatCurrency(v)}</span>;
};

const renderMarginPct = (v) => {
  if (v == null) return <span className="text-gray-400">—</span>;
  const cls = v < 0 ? 'text-red-600 font-semibold' : v < 5 ? 'text-orange-600' : 'text-gray-900';
  return <span className={cls}>{v.toFixed(2)}%</span>;
};

const renderSpark = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return <span className="text-gray-300 text-xs">no data</span>;
  }
  const max = Math.max(...values, 1);
  return (
    <svg width="96" height="20" className="inline-block">
      {values.map((v, i) => {
        const barW = 96 / values.length;
        const h = Math.max(2, (v / max) * 18);
        return (
          <rect
            key={i}
            x={i * barW + 1}
            y={20 - h}
            width={Math.max(1, barW - 2)}
            height={h}
            fill="#3b82f6"
            opacity={0.7}
          />
        );
      })}
    </svg>
  );
};

const renderCell = (value, col) => {
  switch (col.format) {
    case 'number':           return formatNumber(value);
    case 'currency':         return formatCurrency(value);
    case 'currency_precise': return value != null ? `$${Number(value).toFixed(6)}` : '—';
    case 'pct':              return formatPct(value, 2);
    case 'pct_raw':          return value != null ? `${Number(value).toFixed(2)}%` : '—';
    case 'delta_pct':        return renderDeltaPct(value);
    case 'margin':           return renderMargin(value);
    case 'margin_pct':       return renderMarginPct(value);
    case 'duration':         return formatDurationMs(value);
    case 'spark':            return renderSpark(value);
    case 'retry_rate':       return value != null ? Number(value).toFixed(2) : '—';
    case 'drift_pp':         return renderDriftPp(value);
    default:
      if (value == null || value === '') return <span className="text-gray-400">—</span>;
      return String(value);
  }
};

// ═══════════════════════════════════════════════════════════════
// Table view (for customer_health, margin_leakage_intel)
// ═══════════════════════════════════════════════════════════════
function TableView({ report, data, columns }) {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  if (rows.length === 0) {
    return <div className="p-8 text-center text-gray-500">No rows match the current filters.</div>;
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 text-${col.align || 'left'} text-xs font-semibold uppercase tracking-wider text-gray-700 ${
                  col.sticky ? 'sticky left-0 bg-gray-50 z-10' : ''
                } ${col.width || ''}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-blue-50 transition-colors">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 text-${col.align || 'left'} ${
                    col.sticky ? 'sticky left-0 bg-white z-[1] font-medium' : ''
                  }`}
                >
                  {col.key === 'severity' ? renderSeverityPill(row.severity)
                   : col.key === 'new_pill' ? renderNewPill(row.is_new)
                   : renderCell(row[col.key], col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Grid view (for corridor_radar) — country × vendor heatmap
// ═══════════════════════════════════════════════════════════════
function GridView({ report, data }) {
  const cells = Array.isArray(data?.cells) ? data.cells : [];
  if (cells.length === 0) {
    return <div className="p-8 text-center text-gray-500">No corridors meet the minimum-volume threshold.</div>;
  }

  // Pivot cells into country × vendor matrix
  const countries = Array.from(new Set(cells.map((c) => c.country_code))).sort();
  const vendors = Array.from(new Set(cells.map((c) => c.vendor_id))).sort();

  // Keep country_name and vendor_name for labels (take first observed)
  const countryNames = {};
  const vendorNames = {};
  const cellByKey = {};
  cells.forEach((c) => {
    countryNames[c.country_code] = c.country_name || c.country_code;
    vendorNames[c.vendor_id] = c.vendor_name || c.vendor_id;
    cellByKey[`${c.country_code}|${c.vendor_id}`] = c;
  });

  // Color a cell by its slope severity
  const slopeColor = (slope) => {
    if (slope == null) return 'bg-gray-100';
    if (slope <= -0.03) return 'bg-red-500 text-white';
    if (slope <= -0.01) return 'bg-red-300';
    if (slope >= 0.03)  return 'bg-green-500 text-white';
    if (slope >= 0.01)  return 'bg-green-300';
    return 'bg-gray-100';
  };

  return (
    <div className="p-4 overflow-auto">
      <div className="mb-3 flex items-center space-x-4 text-xs text-gray-600">
        <span>Weeks analyzed: <strong>{data.weeks_analyzed}</strong></span>
        <span>Total cells: <strong>{cells.length}</strong></span>
        <span className="flex items-center space-x-2 ml-auto">
          <span className="flex items-center"><span className="w-3 h-3 bg-red-500 inline-block mr-1 rounded-sm"/> degrading</span>
          <span className="flex items-center"><span className="w-3 h-3 bg-gray-100 border inline-block mr-1 rounded-sm"/> stable</span>
          <span className="flex items-center"><span className="w-3 h-3 bg-green-500 inline-block mr-1 rounded-sm"/> improving</span>
        </span>
      </div>

      <table className="border-collapse text-xs">
        <thead>
          <tr>
            <th className="bg-gray-50 border px-2 py-1 sticky left-0 z-10 text-left min-w-[140px]">Country \ Vendor</th>
            {vendors.map((vid) => (
              <th
                key={vid}
                className="bg-gray-50 border px-2 py-1 text-left min-w-[100px] truncate"
                title={vendorNames[vid]}
              >
                <div className="truncate max-w-[120px]">{vendorNames[vid]}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {countries.map((cc) => (
            <tr key={cc}>
              <td className="bg-white border px-2 py-1 sticky left-0 z-[1] font-medium">
                <div className="flex items-center space-x-1">
                  <span className="text-[10px] text-gray-500 font-mono">{cc}</span>
                  <span className="truncate">{countryNames[cc]}</span>
                </div>
              </td>
              {vendors.map((vid) => {
                const c = cellByKey[`${cc}|${vid}`];
                if (!c) {
                  return <td key={vid} className="bg-gray-50 border px-2 py-1 text-gray-300 text-center">—</td>;
                }
                const slopePctPerWeek = c.slope != null ? (c.slope * 100).toFixed(2) : '—';
                const title = `${countryNames[cc]} × ${vendorNames[vid]}\n`
                            + `Latest delivery: ${c.latest_delivery_rate != null ? (c.latest_delivery_rate * 100).toFixed(1) + '%' : 'n/a'}\n`
                            + `Slope: ${slopePctPerWeek} pp/week\n`
                            + `Volume: ${formatNumber(c.total_volume)} over ${c.n_weeks} weeks`;
                return (
                  <td
                    key={vid}
                    className={`border px-2 py-1 cursor-help text-center ${slopeColor(c.slope)}`}
                    title={title}
                  >
                    <div className="leading-tight">
                      <div className="font-semibold">
                        {c.latest_delivery_rate != null ? `${(c.latest_delivery_rate * 100).toFixed(1)}%` : '—'}
                      </div>
                      <div className="text-[10px] opacity-90">{slopePctPerWeek}pp/w</div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Scorecard view (for executive_scorecard) — KPI cards + movers
// ═══════════════════════════════════════════════════════════════
function ScorecardView({ report, data }) {
  if (!data?.kpis) {
    return <div className="p-8 text-center text-gray-500">No data.</div>;
  }

  // Label the three comparison columns based on what the backend tells us
  // (the labels shift when window_weeks > 1 since WoW/MoM semantics no longer apply).
  const labels = data.delta_labels || {
    wow_delta_pct: 'WoW',
    mom_delta_pct: 'MoM',
    yoy_delta_pct: 'YoY',
  };

  const kpiDefs = [
    { key: 'volume',        label: 'Volume',        format: 'number',   icon: BarChart2 },
    { key: 'revenue',       label: 'Revenue',       format: 'currency', icon: DollarSign },
    { key: 'margin',        label: 'Margin',        format: 'currency', icon: TrendingUp },
    { key: 'delivery_rate', label: 'Delivery Rate', format: 'pct',      icon: TrendingUp },
  ];

  const formatKpiValue = (v, fmt) => {
    if (v == null) return '—';
    if (fmt === 'currency') return formatCurrency(v);
    if (fmt === 'pct')      return formatPct(v, 2);
    return formatNumber(v);
  };

  const renderKpiSpark = (spark, fmt) => {
    if (!Array.isArray(spark) || spark.length === 0) return null;
    const values = spark.map((s) => s.v ?? 0);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const W = 160, H = 36;
    const stepX = W / Math.max(values.length - 1, 1);
    const path = values.map((v, i) => {
      const x = i * stepX;
      const y = H - ((v - min) / range) * (H - 4) - 2;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return (
      <svg width={W} height={H} className="mt-2">
        <path d={path} fill="none" stroke="#3b82f6" strokeWidth="1.5" />
      </svg>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiDefs.map(({ key, label, format, icon: Icon }) => {
          const k = data.kpis[key];
          if (!k) return null;
          return (
            <div key={key} className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
                <Icon size={16} className="text-blue-500" />
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {formatKpiValue(k.current, format)}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-gray-400" title={data.comparison_windows?.wow_delta_pct?.join(' → ')}>
                    {labels.wow_delta_pct}
                  </div>
                  <div>{renderDeltaPct(k.wow_delta_pct)}</div>
                </div>
                <div>
                  <div className="text-gray-400" title={data.comparison_windows?.mom_delta_pct?.join(' → ')}>
                    {labels.mom_delta_pct}
                  </div>
                  <div>{renderDeltaPct(k.mom_delta_pct)}</div>
                </div>
                <div>
                  <div className="text-gray-400" title={data.comparison_windows?.yoy_delta_pct?.join(' → ')}>
                    {labels.yoy_delta_pct}
                  </div>
                  <div>{renderDeltaPct(k.yoy_delta_pct)}</div>
                </div>
              </div>
              {renderKpiSpark(k.spark, format)}
            </div>
          );
        })}
      </div>

      {/* Movers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MoverPanel title="Growing customers"    items={data.movers?.customers?.growing} iconDir="up" />
        <MoverPanel title="Shrinking customers"  items={data.movers?.customers?.shrinking} iconDir="down" />
        <MoverPanel title="Growing countries"    items={data.movers?.countries?.growing} iconDir="up" />
        <MoverPanel title="Shrinking countries"  items={data.movers?.countries?.shrinking} iconDir="down" />
      </div>
    </div>
  );
}

function MoverPanel({ title, items, iconDir }) {
  const Icon = iconDir === 'up' ? TrendingUp : TrendingDown;
  const headerColor = iconDir === 'up' ? 'text-green-600' : 'text-red-600';
  const rows = Array.isArray(items) ? items : [];
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="px-4 py-2 border-b flex items-center space-x-2">
        <Icon size={16} className={headerColor} />
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-xs text-gray-400">No movers in this window.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <tr key={r.id || i}>
                <td className="px-3 py-2 truncate max-w-[220px]" title={r.name}>{r.name || r.id}</td>
                <td className="px-3 py-2 text-right text-gray-600">{formatNumber(r.volume_current)}</td>
                <td className="px-3 py-2 text-right">{renderDeltaPct(r.delta_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Time-series view (for latency_decomposition) — Recharts line chart
// ═══════════════════════════════════════════════════════════════
function TimeSeriesView({ report, data, onVendorChange }) {
  const series = Array.isArray(data?.series) ? data.series : [];
  const stages = Array.isArray(data?.stages) ? data.stages : [];
  const vendorOptions = Array.isArray(data?.vendor_options) ? data.vendor_options : [];

  if (series.length === 0) {
    return (
      <div className="p-6 space-y-4">
        {vendorOptions.length > 0 && (
          <VendorPicker
            value={data.vendor_id || ''}
            options={vendorOptions}
            onChange={onVendorChange}
          />
        )}
        <div className="p-8 text-center text-gray-500">
          No data points in this window. Try widening the date range or lowering the min volume threshold.
        </div>
      </div>
    );
  }

  // Color palette pool — cycles through stages as they appear
  const COLOR_POOL = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#06b6d4'];
  const stageColor = (key, idx) => {
    // Back-compat hardcoded keys for V2 Latency Decomposition
    const fixed = {
      platform_ingress_ms: '#3b82f6',
      vendor_ack_ms:       '#8b5cf6',
      e2e_ms:              '#f59e0b',
      delivered_only_ms:   '#10b981',
    };
    return fixed[key] || COLOR_POOL[idx % COLOR_POOL.length];
  };

  // Detect report type to pick y-axis label and value formatter.
  // Rather than hardcoding, default to 'number' and override for known reports.
  const reportId = report?.id;
  let yAxisLabel = '';
  let valueFormatter = (v) => v == null ? '—' : (typeof v === 'number' ? v.toLocaleString() : String(v));

  if (reportId === 'latency_decomposition') {
    yAxisLabel = 'ms';
    valueFormatter = (v) => v == null ? '—' : `${Math.round(v)} ms`;
  } else if (reportId === 'profitability_by_segment') {
    yAxisLabel = '$';
    valueFormatter = (v) => {
      if (v == null) return '—';
      if (typeof v !== 'number') return String(v);
      // margin_pct rendered as percent, others as currency
      return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };
  } else if (reportId === 'vendor_cost_trend') {
    yAxisLabel = '$ / msg';
    valueFormatter = (v) => v == null ? '—' : `$${Number(v).toFixed(5)}`;
  }

  const tooltipFormatter = (value, name) => [valueFormatter(value), name];

  return (
    <div className="p-6 space-y-4">
      {vendorOptions.length > 0 && (
        <VendorPicker
          value={data.vendor_id || ''}
          options={vendorOptions}
          onChange={onVendorChange}
        />
      )}

      {/* F3 price-creep callout */}
      {reportId === 'vendor_cost_trend' && data?.drift_pct != null && (
        <div className={`p-3 rounded border ${
          data.drift_pct > 5 ? 'bg-red-50 border-red-200 text-red-700'
          : data.drift_pct < -5 ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-gray-50 border-gray-200 text-gray-600'
        } text-sm`}>
          <strong>Price drift:</strong>{' '}
          {data.drift_pct > 0 ? '+' : ''}{data.drift_pct}% from window start to window end
          {data.drift_pct > 5 && ' — flag for review'}
          {data.drift_pct < -5 && ' — cost decreasing'}
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">
              {report?.name || 'Time Series'}
              {data.vendor_id ? ` — vendor ${data.vendor_id}` : ''}
            </h3>
            <p className="text-xs text-gray-500">
              {data.granularity === 'week' ? 'Weekly' : 'Daily'} values over {series.length} points
            </p>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={series} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="t" tick={{ fontSize: 11 }} minTickGap={30} />
            <YAxis
              tick={{ fontSize: 11 }}
              label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 11 } : undefined}
            />
            <Tooltip
              formatter={tooltipFormatter}
              labelStyle={{ fontSize: 11, fontWeight: 600 }}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {stages.map((stage, idx) => (
              <Line
                key={stage.key}
                type="monotone"
                dataKey={stage.key}
                name={stage.label}
                stroke={stageColor(stage.key, idx)}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {/* Stage legend with definitions */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {stages.map((stage, idx) => (
            <div key={stage.key} className="flex items-start space-x-2">
              <div
                className="w-3 h-3 rounded-sm mt-0.5 flex-shrink-0"
                style={{ backgroundColor: stageColor(stage.key, idx) }}
              />
              <div>
                <div className="font-medium text-gray-700">{stage.label}</div>
                <div className="text-gray-500 text-[11px]">{stage.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VendorPicker({ value, options, onChange }) {
  return (
    <div className="flex items-center space-x-2 text-sm">
      <span className="text-gray-500">Vendor:</span>
      <select
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
      >
        <option value="">All vendors (aggregate)</option>
        {options.map((v) => (
          <option key={v.id} value={v.id}>{v.name}</option>
        ))}
      </select>
      <span className="text-gray-400 text-xs italic">
        {value ? '(showing single-vendor series)' : '(aggregated across vendors)'}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DualSectionView — renders 2 sections as tables with section toggle
// Used by: C3 expansion_opportunities (new_countries + growth)
// ═══════════════════════════════════════════════════════════════
function DualSectionView({ report, data, sections }) {
  // sections is a list: [ { key, title, description, columns } ]
  const [activeSection, setActiveSection] = useState(sections[0]?.key);

  const current = sections.find((s) => s.key === activeSection) || sections[0];
  const rows = Array.isArray(data?.[current.key]) ? data[current.key] : [];

  return (
    <div className="p-6 space-y-4">
      {/* Section tabs */}
      <div className="flex items-center space-x-1 border-b border-gray-200">
        {sections.map((s) => {
          const count = Array.isArray(data?.[s.key]) ? data[s.key].length : 0;
          const isActive = s.key === activeSection;
          return (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {s.title}
              <span className="ml-2 text-xs text-gray-400">({count})</span>
            </button>
          );
        })}
      </div>

      {current.description && (
        <p className="text-xs text-gray-500 italic">{current.description}</p>
      )}

      {rows.length === 0 ? (
        <div className="p-8 text-center text-gray-400">No rows in this section.</div>
      ) : (
        <TableView
          report={report}
          data={{ rows }}
          columns={current.columns}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HeatmapView — per-customer hour × day-of-week traffic heatmap
// Used by: C4 traffic_fingerprint (single + compare modes)
// ═══════════════════════════════════════════════════════════════
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // dow 1..7

// Color helper — shared between single + compare grids. Per-customer normalized.
function heatmapCellColor(cell, maxVol) {
  if (!cell || !cell.volume || maxVol === 0) return 'bg-gray-50';
  const ratio = cell.volume / maxVol;
  if (ratio > 0.8) return 'bg-emerald-700 text-white';
  if (ratio > 0.6) return 'bg-emerald-600 text-white';
  if (ratio > 0.4) return 'bg-emerald-500 text-white';
  if (ratio > 0.2) return 'bg-emerald-400';
  if (ratio > 0.1) return 'bg-emerald-300';
  if (ratio > 0.02) return 'bg-emerald-200';
  return 'bg-emerald-100';
}

// Build 7×24 matrix from cells array
function buildHeatmapMatrix(cells) {
  const matrix = Array.from({ length: 7 }, () => Array(24).fill(null));
  (cells || []).forEach((c) => {
    const d = (c.day_of_week >= 1 && c.day_of_week <= 7) ? c.day_of_week - 1 : null;
    const h = (c.hour >= 0 && c.hour <= 23) ? c.hour : null;
    if (d !== null && h !== null) matrix[d][h] = c;
  });
  return matrix;
}

// Single-customer grid (full-size cells, full hour labels)
function HeatmapGrid({ title, subtitle, cells, maxVol, totalVol, compact = false }) {
  const matrix = buildHeatmapMatrix(cells);
  const cellMinWidth = compact ? 18 : 28;
  const cellHeight   = compact ? 16 : 22;
  const fontSize     = compact ? '9px' : '10px';
  const showHourLabels = !compact; // compact grids skip hour labels for space

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-3 overflow-x-auto">
      {title && (
        <div className="mb-2">
          <h3 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-700 truncate`} title={title}>
            {title}
          </h3>
          {subtitle && <p className="text-[10px] text-gray-500">{subtitle}</p>}
        </div>
      )}
      <table className="border-collapse" style={{ fontSize }}>
        {showHourLabels && (
          <thead>
            <tr>
              <th className="w-12"></th>
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} className="px-1 py-1 text-gray-500 font-normal" style={{ minWidth: cellMinWidth }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {matrix.map((row, dIdx) => (
            <tr key={dIdx}>
              <td className={`${compact ? 'pr-1 text-[9px]' : 'pr-2'} text-right text-gray-600 font-medium`}>
                {DAY_LABELS[dIdx]}
              </td>
              {row.map((cell, hIdx) => {
                const vol = cell?.volume || 0;
                const dr = cell?.delivery_rate;
                const title = cell
                  ? `${DAY_LABELS[dIdx]} ${hIdx}:00 — ${formatNumber(vol)} msgs${
                      dr != null ? ` · ${(dr * 100).toFixed(1)}% delivery` : ''
                    }`
                  : `${DAY_LABELS[dIdx]} ${hIdx}:00 — no data`;
                return (
                  <td
                    key={hIdx}
                    title={title}
                    className={`border border-white text-center align-middle ${heatmapCellColor(cell, maxVol)}`}
                    style={{ minWidth: cellMinWidth, height: cellHeight }}
                  >
                    {!compact && vol > 0 && vol / maxVol > 0.4 ? formatNumber(vol) : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!compact && (
        <div className="flex items-center space-x-2 mt-4 text-[10px] text-gray-600">
          <span>Less</span>
          <div className="w-4 h-4 bg-emerald-100 border border-gray-200"></div>
          <div className="w-4 h-4 bg-emerald-300"></div>
          <div className="w-4 h-4 bg-emerald-500"></div>
          <div className="w-4 h-4 bg-emerald-700"></div>
          <span>More</span>
          <span className="ml-4 text-gray-400">Max: {formatNumber(maxVol)}</span>
          {totalVol != null && (
            <span className="ml-2 text-gray-400">Total: {formatNumber(totalVol)}</span>
          )}
        </div>
      )}
      {compact && (
        <div className="text-[9px] text-gray-400 mt-1 truncate">
          Max {formatNumber(maxVol)} · Total {formatNumber(totalVol || 0)}
        </div>
      )}
    </div>
  );
}

// Multi-select panel: user picks up to 9 customers from the options list
function CustomerMultiSelect({ options, selected, onChange, onClose }) {
  const [search, setSearch] = useState('');
  const filtered = options.filter((o) =>
    !search || (o.name || '').toLowerCase().includes(search.toLowerCase())
  );
  const selectedSet = new Set(selected);
  const toggle = (id) => {
    if (selectedSet.has(id)) {
      onChange(selected.filter((x) => x !== id));
    } else if (selected.length < 9) {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">Customize customer selection</h4>
        <div className="text-xs text-gray-500">
          {selected.length}/9 selected
        </div>
      </div>
      <input
        type="text"
        placeholder="Search customers…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
      />
      <div className="max-h-60 overflow-y-auto border border-gray-100 rounded">
        {filtered.length === 0 ? (
          <div className="p-3 text-center text-gray-400 text-xs">No matches.</div>
        ) : (
          filtered.map((o) => {
            const checked = selectedSet.has(o.id);
            const disabled = !checked && selected.length >= 9;
            return (
              <label
                key={o.id}
                className={`flex items-center space-x-2 px-2 py-1 text-xs cursor-pointer hover:bg-gray-50 ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(o.id)}
                />
                <span className="flex-1 truncate">{o.name}</span>
                <span className="text-gray-400 text-[10px]">{formatNumber(o.volume || 0)}</span>
              </label>
            );
          })
        )}
      </div>
      <div className="flex justify-between pt-1">
        <button
          onClick={() => onChange([])}
          className="text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Clear (use top N)
        </button>
        <button
          onClick={onClose}
          className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CustomerPicker — single-select searchable dropdown for reports
// that accept one customer (X1, X2, E3).
//
// Props:
//   value       string - selected customer_id (or '')
//   onChange    (id, name) => void  - called on selection
//   asOf        string - 'YYYY-MM-DD' or '' for today
//   windowDays  number - defaults to 30; controls which customers appear in list
//   placeholder string - shown when no selection
//   allowBlank  bool   - if true, adds a "(all / none)" option at top
//   blankLabel  string - text for the blank option (default "All customers")
// ═══════════════════════════════════════════════════════════════
function CustomerPicker({ value, onChange, asOf, windowDays = 30, placeholder, allowBlank = false, blankLabel = 'All customers' }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  // Find the label for the currently-selected value (may not be in options yet if still loading)
  const selectedOption = options.find((o) => o.id === value);
  const displayLabel = selectedOption
    ? selectedOption.name
    : (value ? value : '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (asOf) params.set('as_of', asOf);
        if (windowDays) params.set('window_days', String(windowDays));
        const res = await apiCall(`/api/intelligence/customer-options?${params.toString()}`);
        const data = await res.json();
        if (!cancelled) {
          setOptions(Array.isArray(data?.options) ? data.options : []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load customers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [asOf, windowDays]);

  const filtered = options.filter((o) =>
    !search || (o.name || '').toLowerCase().includes(search.toLowerCase()) || o.id.includes(search)
  );

  const pick = (opt) => {
    onChange && onChange(opt.id, opt.name);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="border border-gray-300 rounded px-2 py-1 text-xs bg-white min-w-[220px] text-left flex items-center justify-between gap-2"
      >
        <span className={`truncate ${displayLabel ? 'text-gray-800' : 'text-gray-400'}`}>
          {displayLabel || placeholder || 'Select customer…'}
        </span>
        <span className="text-gray-400 text-[10px]">▼</span>
      </button>

      {open && (
        <div className="absolute left-0 z-10 mt-1 bg-white border border-gray-200 rounded shadow-lg p-2 w-80 max-h-80 overflow-y-auto">
          <input
            type="text"
            autoFocus
            placeholder="Search by name or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-gray-200 rounded px-2 py-1 text-xs mb-2"
          />
          {loading && <div className="p-3 text-center text-xs text-gray-400">Loading…</div>}
          {error && <div className="p-3 text-center text-xs text-red-500">{error}</div>}
          {!loading && !error && (
            <>
              {allowBlank && (
                <button
                  type="button"
                  onClick={() => pick({ id: '', name: blankLabel })}
                  className="w-full text-left px-2 py-1 text-xs rounded hover:bg-blue-50 text-gray-600 italic"
                >
                  {blankLabel}
                </button>
              )}
              {filtered.length === 0 ? (
                <div className="p-3 text-center text-xs text-gray-400">No matches.</div>
              ) : (
                filtered.slice(0, 200).map((o) => (
                  <button
                    type="button"
                    key={o.id}
                    onClick={() => pick(o)}
                    className={`w-full text-left px-2 py-1 text-xs rounded hover:bg-blue-50 flex items-center justify-between ${
                      o.id === value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <span className="truncate flex-1">{o.name}</span>
                    <span className="text-gray-400 text-[10px] ml-2">{formatNumber(o.volume || 0)}</span>
                  </button>
                ))
              )}
              {filtered.length > 200 && (
                <div className="p-2 text-center text-[10px] text-gray-400 italic">
                  Showing first 200 of {filtered.length} matches — narrow your search
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HeatmapView({ report, data, params, onCustomerChange, onModeChange, onTopNChange, onCustomerIdsChange, onNormChange }) {
  // User intent drives the UI; response data provides content.
  // Mode, selected customer, and selected IDs come from params (live state),
  // falling back to response data on first render before any state change.
  const mode = (params?.fingerprint_mode) || (data?.mode === 'compare' ? 'compare' : 'single');
  const options = Array.isArray(data?.customer_options) ? data.customer_options : [];
  const [showCustomize, setShowCustomize] = useState(false);

  // Mode toggle — always visible
  const ModeToggle = () => (
    <div className="flex items-center space-x-1 text-sm">
      <button
        onClick={() => onModeChange && onModeChange('single')}
        className={`px-3 py-1 text-xs rounded border ${
          mode === 'single' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
        }`}
      >
        Single
      </button>
      <button
        onClick={() => onModeChange && onModeChange('compare')}
        className={`px-3 py-1 text-xs rounded border ${
          mode === 'compare' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
        }`}
      >
        Compare
      </button>
      <span className="text-gray-400 text-[11px] italic ml-2">
        {mode === 'single' ? 'Single customer heatmap' : 'Up to 9 customers side-by-side (per-customer normalized)'}
      </span>
    </div>
  );

  // ─── COMPARE MODE ──────────────────────────
  if (mode === 'compare') {
    const customers = Array.isArray(data?.customers) ? data.customers : [];
    // User intent: whatever the user has ticked in params. Falls back to
    // the last-loaded customers only when the user hasn't made a selection.
    const selectedIds = (Array.isArray(params?.fingerprint_customer_ids) && params.fingerprint_customer_ids.length > 0)
      ? params.fingerprint_customer_ids
      : customers.map((c) => c.customer_id);

    // Normalization mode — drives cell-color scale. Local state since it
    // doesn't need to round-trip to the backend (computed from response data).
    const normMode = params?.fingerprint_norm || 'per_customer';
    const globalMax = customers.reduce((m, c) => Math.max(m, c.max_volume || 0), 0);

    return (
      <div className="p-6 space-y-4">
        <ModeToggle />

        {/* Customize controls */}
        <div className="flex items-center space-x-2 text-sm">
          <button
            onClick={() => setShowCustomize((s) => !s)}
            className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
          >
            {showCustomize ? 'Hide customize' : 'Customize selection'}
          </button>
          <span className="text-gray-500 text-xs">
            {data?.selection_mode === 'custom'
              ? `Showing ${customers.length} user-selected customer${customers.length === 1 ? '' : 's'}`
              : `Showing top ${customers.length} by volume`}
          </span>
        </div>

        {showCustomize && (
          <CustomerMultiSelect
            options={options}
            selected={selectedIds}
            onChange={(ids) => onCustomerIdsChange && onCustomerIdsChange(ids)}
            onClose={() => setShowCustomize(false)}
          />
        )}

        {customers.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No customer data in this window. Try widening the window.
          </div>
        ) : (
          <>
            {/* Normalization toggle */}
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-500 text-xs">Color scale:</span>
              <button
                onClick={() => onNormChange && onNormChange('per_customer')}
                className={`px-3 py-1 text-xs rounded border ${
                  normMode === 'per_customer' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                Per-customer (pattern)
              </button>
              <button
                onClick={() => onNormChange && onNormChange('global')}
                className={`px-3 py-1 text-xs rounded border ${
                  normMode === 'global' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                Global (magnitude)
              </button>
              <span className="text-gray-400 text-[11px] italic ml-2">
                {normMode === 'per_customer'
                  ? 'Each heatmap scaled to its own max — reveals shape'
                  : `All heatmaps share global max of ${formatNumber(globalMax)} — reveals magnitude`}
              </span>
            </div>

            <p className="text-xs text-gray-500 italic">
              {data?.window && `Window: ${data.window[0]} → ${data.window[1]}.`}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {customers.map((c) => (
                <HeatmapGrid
                  key={c.customer_id}
                  title={c.customer_name || c.customer_id}
                  subtitle={`Max ${formatNumber(c.max_volume || 0)}`}
                  cells={c.cells}
                  maxVol={normMode === 'global' ? globalMax : (c.max_volume || 0)}
                  totalVol={c.total_volume}
                  compact
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── SINGLE MODE ──────────────────────────
  const cells = Array.isArray(data?.cells) ? data.cells : [];
  const maxVol = data?.max_volume || 0;
  // User intent for the dropdown selection — falls back to response echo.
  const selected = (params?.customer_id) || data?.customer_id || '';

  return (
    <div className="p-6 space-y-4">
      <ModeToggle />

      <div className="flex items-center space-x-2 text-sm">
        <span className="text-gray-500">Customer:</span>
        <select
          value={selected}
          onChange={(e) => onCustomerChange && onCustomerChange(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-xs bg-white min-w-[260px]"
        >
          <option value="">— Select a customer —</option>
          {options.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} {c.volume ? `(${formatNumber(c.volume)})` : ''}
            </option>
          ))}
        </select>
        {data?.total_volume ? (
          <span className="text-gray-400 text-xs italic">
            Total: {formatNumber(data.total_volume)} over {data.window?.[0]} → {data.window?.[1]}
          </span>
        ) : null}
      </div>

      {!selected ? (
        <div className="p-8 text-center text-gray-400">
          Select a customer above to see their traffic fingerprint.
        </div>
      ) : cells.length === 0 ? (
        <div className="p-8 text-center text-gray-400">
          No traffic data for this customer in the selected window.
        </div>
      ) : (
        <HeatmapGrid
          title={`${data.customer_name || selected} — Traffic Fingerprint`}
          subtitle="Each cell shows total volume for that hour × day. Darker = more traffic. Hover for details."
          cells={cells}
          maxVol={maxVol}
          totalVol={data.total_volume}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ConcentrationView — HHI scorecard + per-customer table
// Used by: E2 concentration_risk
// ═══════════════════════════════════════════════════════════════
function ConcentrationView({ report, data }) {
  const summary = data?.summary || {};
  const customers = Array.isArray(data?.customers) ? data.customers : [];

  const levelBadge = (level) => {
    if (level === 'highly_concentrated') return { cls: 'bg-red-100 text-red-700', text: 'Highly concentrated' };
    if (level === 'moderately_concentrated') return { cls: 'bg-yellow-100 text-yellow-700', text: 'Moderately concentrated' };
    return { cls: 'bg-emerald-100 text-emerald-700', text: 'Unconcentrated' };
  };
  const badge = levelBadge(summary.concentration_level);

  return (
    <div className="p-6 space-y-4">
      {/* Summary scorecard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">HHI</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {summary.hhi != null ? formatNumber(summary.hhi) : '—'}
          </div>
          <div className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
            {badge.text}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">
            Top {summary.top_n || 10} share
          </div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {summary.top_n_share_pct != null ? `${summary.top_n_share_pct.toFixed(1)}%` : '—'}
          </div>
          <div className="text-xs text-gray-400 mt-2">of total revenue</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">
            Customers to 80%
          </div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {summary.customers_to_80_pct != null ? formatNumber(summary.customers_to_80_pct) : '—'}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            of {formatNumber(summary.total_customers || 0)} total
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Total Revenue</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {formatCurrency(summary.total_revenue || 0)}
          </div>
          <div className="text-xs text-gray-400 mt-2">across window</div>
        </div>
      </div>

      {/* HHI interpretation note */}
      <p className="text-xs text-gray-500 italic px-1">
        HHI (Herfindahl-Hirschman Index) measures concentration on a 0–10,000 scale.
        Lower values mean more distributed revenue; &lt;1500 is unconcentrated,
        1500–2500 is moderate, &gt;2500 is highly concentrated (DOJ merger guidelines, adapted).
      </p>

      {/* Customers table */}
      {customers.length === 0 ? (
        <div className="p-8 text-center text-gray-400">No customer revenue in this window.</div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Manager</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Share %</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cumulative %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map((c, i) => {
                // Highlight rows that are in the "top 80%" cohort
                const highlight = c.cumulative_share_pct != null && c.cumulative_share_pct <= 80.0;
                return (
                  <tr key={c.customer_id || i} className={highlight ? 'bg-amber-50' : ''}>
                    <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">{c.customer_name || c.customer_id}</td>
                    <td className="px-3 py-2 text-gray-600">{c.account_manager || '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{formatNumber(c.volume)}</td>
                    <td className="px-3 py-2 text-right text-gray-800 font-medium">{formatCurrency(c.revenue)}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{c.share_pct?.toFixed(2)}%</td>
                    <td className="px-3 py-2 text-right text-gray-500">{c.cumulative_share_pct?.toFixed(2)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CustomerHealthView — X1 self-service summary + per-corridor table
// ═══════════════════════════════════════════════════════════════
function CustomerHealthView({ report, data }) {
  const summary = data?.summary;
  const corridors = Array.isArray(data?.corridors) ? data.corridors : [];

  if (!summary) {
    return (
      <div className="p-6 space-y-3">
        <div className="p-8 text-center text-gray-400">
          No data for this customer in the selected window.
        </div>
      </div>
    );
  }

  const fmtPct = (v) => v == null ? '—' : `${(v * 100).toFixed(2)}%`;
  const fmtDelta = (v) => {
    if (v == null) return null;
    const sign = v > 0 ? '+' : '';
    const cls = v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : 'text-gray-500';
    return <span className={`text-xs font-medium ${cls}`}>{sign}{v.toFixed(2)}%</span>;
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header: customer context */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-lg font-semibold text-gray-800">
          {summary.customer_name || summary.customer_id}
        </h2>
        <p className="text-xs text-gray-500">
          Account manager: {summary.account_manager || '—'} ·
          Window {data.current_window?.[0]} → {data.current_window?.[1]}
        </p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Volume</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {formatNumber(summary.volume_current || 0)}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            vs baseline {formatNumber(summary.volume_baseline_scaled || 0)} {fmtDelta(summary.volume_delta_pct)}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Delivery Rate</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {fmtPct(summary.delivery_rate_current)}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            vs baseline {fmtPct(summary.delivery_rate_baseline)} {fmtDelta(summary.delivery_rate_delta_pct)}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Revenue</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {formatCurrency(summary.revenue_current || 0)}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            vs baseline {formatCurrency(summary.revenue_baseline_scaled || 0)} {fmtDelta(summary.revenue_delta_pct)}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Error Rate</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {fmtPct(summary.error_rate_current)}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {summary.retries_current != null && `${formatNumber(summary.retries_current)} retries`}
          </div>
        </div>
      </div>

      {/* Latency row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: 'platform_ingress_ms', label: 'Platform Ingress' },
          { k: 'vendor_ack_ms',       label: 'Vendor ACK' },
          { k: 'e2e_ms_current',      label: 'End-to-End', baseKey: 'e2e_ms_baseline' },
          { k: 'delivered_only_ms',   label: 'Delivered-Only' },
        ].map((item) => {
          const v = summary[item.k];
          const base = item.baseKey ? summary[item.baseKey] : null;
          return (
            <div key={item.k} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-[11px] text-gray-500 uppercase tracking-wider">{item.label}</div>
              <div className="text-lg font-semibold text-gray-700 mt-1">
                {v != null ? `${Math.round(v)} ms` : '—'}
              </div>
              {base != null && (
                <div className="text-[10px] text-gray-400">
                  baseline {Math.round(base)} ms
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Corridors table */}
      {corridors.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Traffic by country</h3>
            <p className="text-xs text-gray-500">Top {corridors.length} destinations in this window</p>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg E2E</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {corridors.map((c, i) => (
                <tr key={c.country_code || i}>
                  <td className="px-3 py-2 font-medium text-gray-800">
                    {c.country_name || c.country_code}
                    {c.country_code && c.country_name && (
                      <span className="text-[10px] text-gray-400 ml-1">({c.country_code})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{formatNumber(c.volume)}</td>
                  <td className="px-3 py-2 text-right">
                    {c.delivery_rate != null ? `${(c.delivery_rate * 100).toFixed(2)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {c.avg_e2e_ms != null ? `${Math.round(c.avg_e2e_ms)} ms` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">{formatCurrency(c.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SlaView — X2 SLA vs Reality. Summary scorecard + dual-line chart
// ═══════════════════════════════════════════════════════════════
function SlaView({ report, data }) {
  const summary = data?.summary || {};
  const series = Array.isArray(data?.series) ? data.series : [];
  const targets = summary.sla_targets || {};
  const passRate = summary.pass_rate_pct || {};
  const breaches = summary.breaches || {};

  const passBadge = (pct) => {
    if (pct == null) return { cls: 'bg-gray-100 text-gray-500', label: '—' };
    if (pct >= 95)   return { cls: 'bg-emerald-100 text-emerald-700', label: `${pct.toFixed(1)}% pass` };
    if (pct >= 80)   return { cls: 'bg-yellow-100 text-yellow-700',   label: `${pct.toFixed(1)}% pass` };
    return                  { cls: 'bg-red-100 text-red-700',         label: `${pct.toFixed(1)}% pass` };
  };

  // Inject target lines into series (Recharts needs numeric value per point)
  const chartData = series.map((p) => ({
    ...p,
    target_delivery_rate: targets.delivery_rate_pct,
    target_error_rate: targets.error_rate_pct,
    target_e2e_ms: targets.e2e_ms,
  }));

  return (
    <div className="p-6 space-y-4">
      {/* Customer context */}
      {data.customer_name && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-800">{data.customer_name}</h2>
          <p className="text-xs text-gray-500">
            Window {data.window?.[0]} → {data.window?.[1]} · {summary.total_periods || 0} periods
          </p>
        </div>
      )}

      {/* Pass-rate scorecards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { key: 'delivery', label: 'Delivery Rate', target: `≥ ${targets.delivery_rate_pct}%` },
          { key: 'e2e',      label: 'End-to-End Latency', target: `≤ ${targets.e2e_ms} ms` },
          { key: 'error',    label: 'Error Rate', target: `≤ ${targets.error_rate_pct}%` },
        ].map(({ key, label, target }) => {
          const badge = passBadge(passRate[key]);
          return (
            <div key={key} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
              <div className="text-sm text-gray-600 mt-1">Target {target}</div>
              <div className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                {badge.label}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {breaches[key] || 0} breach{(breaches[key] || 0) === 1 ? '' : 'es'} this window
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      {series.length === 0 ? (
        <div className="p-8 text-center text-gray-400">
          No data in this window. Customer may be inactive.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Delivery rate chart */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Delivery Rate vs Target</h3>
            <p className="text-[11px] text-gray-500 mb-3">% delivered, target shown as dashed line</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="delivery_rate" name="Observed" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="target_delivery_rate" name="Target" stroke="#94a3b8" strokeDasharray="4 4" dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* E2E latency chart */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">End-to-End Latency vs Target</h3>
            <p className="text-[11px] text-gray-500 mb-3">ms, target shown as dashed ceiling</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="e2e_ms" name="Observed (ms)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="target_e2e_ms" name="Target ceiling" stroke="#94a3b8" strokeDasharray="4 4" dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Error rate chart */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Error Rate vs Target</h3>
            <p className="text-[11px] text-gray-500 mb-3">% failed, target shown as dashed ceiling</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="error_rate" name="Observed" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="target_error_rate" name="Target ceiling" stroke="#94a3b8" strokeDasharray="4 4" dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DecompositionView — E3 seasonality decomposition
// ═══════════════════════════════════════════════════════════════
function DecompositionView({ report, data }) {
  const summary = data?.summary || {};
  const series = Array.isArray(data?.series) ? data.series : [];
  const metric = data?.metric || 'volume';

  // Format helpers by metric
  const fmtMetric = (v) => {
    if (v == null) return '—';
    if (metric === 'revenue') return formatCurrency(v);
    if (metric === 'delivery_rate') return `${v.toFixed(2)}%`;
    return formatNumber(Math.round(v));
  };

  // Insufficient-data gate
  if (summary.insufficient_data) {
    return (
      <div className="p-6 space-y-4">
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm">
          <strong>Insufficient data:</strong> {summary.data_points} daily points in window; need at least {summary.minimum_required} for decomposition. Try widening the window.
        </div>
      </div>
    );
  }

  const trendBadge = (() => {
    const dir = summary.trend_direction;
    if (dir === 'growing')   return { cls: 'bg-emerald-100 text-emerald-700', label: '↑ Growing' };
    if (dir === 'shrinking') return { cls: 'bg-red-100 text-red-700',         label: '↓ Shrinking' };
    return                          { cls: 'bg-gray-100 text-gray-600',        label: '→ Flat' };
  })();

  const strengthBadge = (s) => {
    if (s == null) return { cls: 'bg-gray-100 text-gray-500', label: '—' };
    if (s >= 0.6)  return { cls: 'bg-blue-100 text-blue-700', label: 'Strong' };
    if (s >= 0.3)  return { cls: 'bg-yellow-100 text-yellow-700', label: 'Moderate' };
    return                { cls: 'bg-gray-100 text-gray-500', label: 'Weak' };
  };
  const sBadge = strengthBadge(summary.seasonality_strength);

  const metricLabels = {
    volume: 'Volume (msgs)',
    revenue: 'Revenue ($)',
    delivery_rate: 'Delivery Rate (%)',
  };

  // Seasonal bar chart data (day of week)
  const dowOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dowData = dowOrder.map((d) => ({
    day: d,
    lift: (summary.dow_seasonal && summary.dow_seasonal[d]) || 0,
  }));

  return (
    <div className="p-6 space-y-4">
      {/* Context row */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-lg font-semibold text-gray-800">
          {metricLabels[metric]} decomposition
          {data.customer_name && ` — ${data.customer_name}`}
        </h2>
        <p className="text-xs text-gray-500">
          Window {data.window?.[0]} → {data.window?.[1]} · {summary.data_points} daily points
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Trend direction</div>
          <div className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${trendBadge.cls}`}>
            {trendBadge.label}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            {summary.trend_change_pct != null
              ? `${summary.trend_change_pct > 0 ? '+' : ''}${summary.trend_change_pct}% over window`
              : '—'}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Seasonality strength</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {summary.seasonality_strength != null ? summary.seasonality_strength.toFixed(2) : '—'}
          </div>
          <div className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${sBadge.cls}`}>
            {sBadge.label}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Peak day</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {summary.peak_day_of_week || '—'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {summary.peak_day_lift != null ? `+${fmtMetric(summary.peak_day_lift)} vs avg` : ''}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Trough day</div>
          <div className="text-2xl font-semibold text-gray-800 mt-1">
            {summary.trough_day_of_week || '—'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {summary.trough_day_dip != null ? `${fmtMetric(summary.trough_day_dip)} vs avg` : ''}
          </div>
        </div>
      </div>

      {/* Day-of-week bars */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Weekly pattern (day-of-week lift vs baseline)</h3>
        <p className="text-[11px] text-gray-500 mb-3">
          Positive = that day typically runs higher than average; negative = typically lower
        </p>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={dowData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => fmtMetric(v)} contentStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="lift" stroke="#3b82f6" strokeWidth={2}
                  dot={{ r: 4 }} activeDot={{ r: 6 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Decomposition chart */}
      {series.length === 0 ? (
        <div className="p-8 text-center text-gray-400">No series data.</div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Decomposition over time</h3>
          <p className="text-[11px] text-gray-500 mb-3">
            Observed = trend + seasonal + residual. Trend shows smoothed direction, seasonal is the weekly cycle, residual is unexplained variance.
          </p>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={series} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={30} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtMetric(v)} contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="observed" name="Observed" stroke="#94a3b8" strokeWidth={1.5}
                    dot={{ r: 1 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="trend" name="Trend" stroke="#3b82f6" strokeWidth={2.5}
                    dot={false} connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="seasonal" name="Seasonal" stroke="#10b981" strokeWidth={1.5}
                    dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="residual" name="Residual" stroke="#ef4444" strokeWidth={1}
                    dot={{ r: 1 }} connectNulls={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <div className="flex items-start space-x-2">
              <div className="w-3 h-3 rounded-sm mt-0.5 bg-[#94a3b8]" />
              <div>
                <div className="font-medium text-gray-700">Observed</div>
                <div className="text-gray-500 text-[11px]">Raw daily value</div>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-3 h-3 rounded-sm mt-0.5 bg-[#3b82f6]" />
              <div>
                <div className="font-medium text-gray-700">Trend</div>
                <div className="text-gray-500 text-[11px]">7-day centered moving average — smoothed direction</div>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-3 h-3 rounded-sm mt-0.5 bg-[#10b981]" />
              <div>
                <div className="font-medium text-gray-700">Seasonal</div>
                <div className="text-gray-500 text-[11px]">Weekly cycle — day-of-week deviation from trend</div>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-3 h-3 rounded-sm mt-0.5 bg-[#ef4444]" />
              <div>
                <div className="font-medium text-gray-700">Residual</div>
                <div className="text-gray-500 text-[11px]">What's left — anomalies live here</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main IntelligenceLibrary component
// ═══════════════════════════════════════════════════════════════
const IntelligenceLibrary = () => {
  const [catalog, setCatalog] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  // Intelligence reports carry their own query params; a shared "as of" date
  // plus report-specific adjustments are applied inside executeReport.
  const [asOf, setAsOf] = useState('');  // empty string = server default (today)

  // Report-specific tuning knobs. Each report only reads the keys it cares about.
  const [reportParams, setReportParams] = useState({
    min_volume: '',          // C1, F1, V1
    min_weekly_volume: '',   // O1
    min_weeks: '',           // O1
    only_negative: true,     // F1
    window_days: '',         // F1, V2
    window_weeks: 1,         // E1
    granularity: 'day',      // V2
    vendor_id: '',           // V2
    min_error_count: '',     // V3
    min_baseline_volume: '', // C2
    min_drop_pct: '',        // C2
    min_new_country_volume: '', // C3
    min_growth_volume: '',   // C3
    min_growth_pct: '',      // C3
    customer_id: '',         // C4
    segment: 'am',           // F2
    top_n: 5,                // F2, E2
    fingerprint_mode: 'single',  // C4: 'single' | 'compare'
    fingerprint_top_n: 6,    // C4 compare
    fingerprint_customer_ids: [], // C4 compare custom selection
    fingerprint_norm: 'per_customer', // C4 compare: 'per_customer' | 'global'
    // Self-service (X1, X2)
    ss_customer_id: '',      // X1, X2: customer picker for AM use
    sla_delivery_rate: 95,   // X2: percent form, UI-friendly
    sla_e2e_ms: 5000,        // X2
    sla_error_rate: 2,       // X2: percent
    decomp_metric: 'volume', // E3: 'volume' | 'revenue' | 'delivery_rate'
    decomp_customer_id: '',  // E3: optional customer filter
    storm_multiplier: '',    // O4
    storm_min_retries: '',   // O4
    storm_min_volume: '',    // O4
  });
  const setParam = (k, v) => setReportParams((p) => ({ ...p, [k]: v }));

  // Customer-scoped detection comes from the shared AuthContext. Used to
  // auto-populate X1/X2 picker and for UX hints. Backend enforces scope
  // regardless.
  const { user, isCustomer } = useAuth();
  const scopedCustomerId =
    isCustomer && user?.customer_id != null ? String(user.customer_id) : null;

  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/api/intelligence/catalog');
        const data = await res.json();
        setCatalog(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load intelligence catalog:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // When the auth context resolves a bound customer, pre-populate the
  // self-service / decomposition customer pickers so they don't hit the
  // "pick a customer" preflight block.
  useEffect(() => {
    if (!scopedCustomerId) return;
    setReportParams((p) => ({
      ...p,
      ss_customer_id: p.ss_customer_id || scopedCustomerId,
      decomp_customer_id: p.decomp_customer_id || scopedCustomerId,
    }));
  }, [scopedCustomerId]);

  const endpointFor = (reportId) => {
    switch (reportId) {
      case 'customer_health':        return '/api/intelligence/customer-health';
      case 'corridor_radar':         return '/api/intelligence/corridor-radar';
      case 'margin_leakage_intel':   return '/api/intelligence/margin-leakage';
      case 'executive_scorecard':    return '/api/intelligence/executive-scorecard';
      case 'vendor_reliability':     return '/api/intelligence/vendor-reliability';
      case 'latency_decomposition':  return '/api/intelligence/latency-decomposition';
      case 'error_code_drift':       return '/api/intelligence/error-code-drift';
      case 'churn_radar':            return '/api/intelligence/churn-radar';
      case 'expansion_opportunities':return '/api/intelligence/expansion-opportunities';
      case 'traffic_fingerprint':    return '/api/intelligence/traffic-fingerprint';
      case 'profitability_by_segment':return '/api/intelligence/profitability-by-segment';
      case 'vendor_cost_trend':      return '/api/intelligence/vendor-cost-trend';
      case 'concentration_risk':     return '/api/intelligence/concentration-risk';
      case 'customer_self_service':  return '/api/intelligence/my-account-health';
      case 'sla_vs_reality':         return '/api/intelligence/sla-vs-reality';
      case 'seasonality_decomposition': return '/api/intelligence/seasonality-decomposition';
      case 'retry_storm_history':    return '/api/intelligence/retry-storm-history';
      default: return null;
    }
  };

  const executeReport = useCallback(async (report) => {
    setActiveReport(report);
    setReportError(null);
    setReportLoading(true);
    setReportData(null);

    let ep = endpointFor(report.id);
    if (!ep) {
      setReportError(`No endpoint mapped for ${report.id}`);
      setReportLoading(false);
      return;
    }

    // C4: switch to compare endpoint when user picks compare mode
    if (report.id === 'traffic_fingerprint' && reportParams.fingerprint_mode === 'compare') {
      ep = '/api/intelligence/traffic-fingerprint-compare';
    }

    // X1/X2 preflight: these require an explicit customer for internal users.
    // Avoid firing a doomed request; show a friendly hint instead.
    // (Customer-scoped JWTs are forced server-side, so this only affects AM/internal flow.)
    const requiresCustomer = (report.id === 'customer_self_service' || report.id === 'sla_vs_reality');
    if (requiresCustomer && !reportParams.ss_customer_id) {
      setReportError('Please pick a customer from the dropdown above to run this report.');
      setReportLoading(false);
      return;
    }

    const params = new URLSearchParams();
    if (asOf) params.set('as_of', asOf);

    // Report-specific knobs — only send if user has set them or for checkbox defaults
    const rp = reportParams;
    switch (report.id) {
      case 'customer_health':
        if (rp.min_volume !== '' && rp.min_volume != null) params.set('min_volume', rp.min_volume);
        break;
      case 'corridor_radar':
        if (rp.min_weekly_volume !== '' && rp.min_weekly_volume != null) params.set('min_weekly_volume', rp.min_weekly_volume);
        if (rp.min_weeks !== '' && rp.min_weeks != null) params.set('min_weeks', rp.min_weeks);
        break;
      case 'margin_leakage_intel':
        if (rp.min_volume !== '' && rp.min_volume != null) params.set('min_volume', rp.min_volume);
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        params.set('only_negative', rp.only_negative ? 'true' : 'false');
        break;
      case 'executive_scorecard':
        if (rp.window_weeks) params.set('window_weeks', rp.window_weeks);
        break;
      case 'vendor_reliability':
        if (rp.min_volume !== '' && rp.min_volume != null) params.set('min_volume', rp.min_volume);
        break;
      case 'latency_decomposition':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.granularity) params.set('granularity', rp.granularity);
        if (rp.vendor_id) params.set('vendor_id', rp.vendor_id);
        break;
      case 'error_code_drift':
        if (rp.min_error_count !== '' && rp.min_error_count != null) params.set('min_error_count', rp.min_error_count);
        break;
      case 'churn_radar':
        if (rp.min_baseline_volume !== '' && rp.min_baseline_volume != null) params.set('min_baseline_volume', rp.min_baseline_volume);
        if (rp.min_drop_pct !== '' && rp.min_drop_pct != null) params.set('min_drop_pct', rp.min_drop_pct);
        break;
      case 'expansion_opportunities':
        if (rp.min_new_country_volume !== '' && rp.min_new_country_volume != null) params.set('min_new_country_volume', rp.min_new_country_volume);
        if (rp.min_growth_volume !== '' && rp.min_growth_volume != null) params.set('min_growth_volume', rp.min_growth_volume);
        if (rp.min_growth_pct !== '' && rp.min_growth_pct != null) params.set('min_growth_pct', rp.min_growth_pct);
        break;
      case 'traffic_fingerprint':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.fingerprint_mode === 'compare') {
          // Compare mode: send top_n and/or customer_ids
          if (Array.isArray(rp.fingerprint_customer_ids) && rp.fingerprint_customer_ids.length > 0) {
            params.set('customer_ids', rp.fingerprint_customer_ids.join(','));
          } else if (rp.fingerprint_top_n) {
            params.set('top_n', rp.fingerprint_top_n);
          }
        } else {
          // Single mode
          if (rp.customer_id) params.set('customer_id', rp.customer_id);
        }
        break;
      case 'profitability_by_segment':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.granularity) params.set('granularity', rp.granularity);
        if (rp.segment) params.set('segment', rp.segment);
        if (rp.top_n) params.set('top_n', rp.top_n);
        break;
      case 'vendor_cost_trend':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.granularity) params.set('granularity', rp.granularity);
        if (rp.vendor_id) params.set('vendor_id', rp.vendor_id);
        break;
      case 'concentration_risk':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.top_n) params.set('top_n', rp.top_n);
        break;
      case 'customer_self_service':
        if (rp.ss_customer_id) params.set('customer_id', rp.ss_customer_id);
        break;
      case 'sla_vs_reality':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.granularity) params.set('granularity', rp.granularity);
        if (rp.ss_customer_id) params.set('customer_id', rp.ss_customer_id);
        // Convert UI percent → 0-1 for backend
        if (rp.sla_delivery_rate !== '' && rp.sla_delivery_rate != null) {
          params.set('sla_delivery_rate', String((parseFloat(rp.sla_delivery_rate) || 95) / 100));
        }
        if (rp.sla_e2e_ms !== '' && rp.sla_e2e_ms != null) params.set('sla_e2e_ms', rp.sla_e2e_ms);
        if (rp.sla_error_rate !== '' && rp.sla_error_rate != null) {
          params.set('sla_error_rate', String((parseFloat(rp.sla_error_rate) || 2) / 100));
        }
        break;
      case 'seasonality_decomposition':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.decomp_metric) params.set('metric', rp.decomp_metric);
        if (rp.decomp_customer_id) params.set('customer_id', rp.decomp_customer_id);
        break;
      case 'retry_storm_history':
        if (rp.window_days !== '' && rp.window_days != null) params.set('window_days', rp.window_days);
        if (rp.storm_multiplier !== '' && rp.storm_multiplier != null) params.set('multiplier', rp.storm_multiplier);
        if (rp.storm_min_retries !== '' && rp.storm_min_retries != null) params.set('min_retries', rp.storm_min_retries);
        if (rp.storm_min_volume !== '' && rp.storm_min_volume != null) params.set('min_volume', rp.storm_min_volume);
        break;
      default: break;
    }

    try {
      const res = await apiCall(`${ep}?${params.toString()}`);
      if (!res.ok) {
        // Try to read FastAPI's {detail: "..."} body for a human-readable message
        let msg = `HTTP ${res.status}`;
        try {
          const errBody = await res.json();
          if (errBody?.detail) msg = errBody.detail;
        } catch (_) {
          // Non-JSON body; keep the HTTP status message
        }
        throw new Error(msg);
      }
      const json = await res.json();
      setReportData(json);
      // If user didn't explicitly pick a date, sync the picker to whatever the
      // server resolved (typically max(event_date) for historical datasets).
      if (!asOf && json.as_of) {
        setAsOf(json.as_of);
      }
    } catch (err) {
      console.error('Intelligence report failed:', err);
      setReportError(err.message);
    } finally {
      setReportLoading(false);
    }
  }, [asOf, reportParams]);

  // ─── C4 auto-run: mode toggle and customer picker feel immediate ───
  // Watches the 3 traffic-fingerprint params that should auto-refetch.
  // Skips the very first render (the initial report selection already fetches).
  const didMountFingerprint = useRef(false);
  useEffect(() => {
    if (activeReport?.id !== 'traffic_fingerprint') {
      didMountFingerprint.current = false; // reset when switching away
      return;
    }
    if (!didMountFingerprint.current) {
      didMountFingerprint.current = true;
      return; // first time we see this report — don't re-fetch, the click already fetched
    }
    executeReport(activeReport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reportParams.fingerprint_mode,
    reportParams.customer_id,
    // Array identity — .join() yields the same string for equivalent selections
    Array.isArray(reportParams.fingerprint_customer_ids) ? reportParams.fingerprint_customer_ids.join(',') : '',
  ]);

  const exportCSV = () => {
    if (!reportData) return;
    let rows = [];
    if (reportData.rows)      rows = reportData.rows;
    else if (reportData.cells) rows = reportData.cells;
    if (rows.length === 0) return;

    const keys = Object.keys(rows[0]);
    const csv = [
      keys.join(','),
      ...rows.map((r) => keys.map((k) => {
        const v = r[k];
        if (Array.isArray(v)) return `"${v.join('; ')}"`;
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeReport?.id || 'intelligence'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Filtered catalog ──────────────────────────────────────
  const filteredCatalog = catalog.filter((r) => {
    if (activeCategory !== 'all' && r.category !== activeCategory) return false;
    if (searchTerm) {
      const hay = `${r.name} ${r.description} ${(r.tags || []).join(' ')}`.toLowerCase();
      if (!hay.includes(searchTerm.toLowerCase())) return false;
    }
    return true;
  });

  // ─── Report detail view ─────────────────────────────────────
  if (activeReport) {
    const renderBody = () => {
      if (reportLoading) return <div className="p-12 text-center text-gray-500"><RefreshCw className="animate-spin inline" /> Running…</div>;
      if (reportError)   return <div className="p-6 text-center text-red-600 bg-red-50 border border-red-200 rounded">Error: {reportError}</div>;
      if (!reportData)   return <div className="p-8 text-center text-gray-400">No data yet.</div>;

      switch (activeReport.view_type) {
        case 'table':
          if (activeReport.id === 'customer_health')      return <TableView report={activeReport} data={reportData} columns={CUSTOMER_HEALTH_COLUMNS} />;
          if (activeReport.id === 'margin_leakage_intel') return <TableView report={activeReport} data={reportData} columns={MARGIN_LEAKAGE_COLUMNS} />;
          if (activeReport.id === 'vendor_reliability')   return <TableView report={activeReport} data={reportData} columns={VENDOR_RELIABILITY_COLUMNS} />;
          if (activeReport.id === 'error_code_drift')     return <TableView report={activeReport} data={reportData} columns={ERROR_CODE_DRIFT_COLUMNS} />;
          if (activeReport.id === 'churn_radar')          return <TableView report={activeReport} data={reportData} columns={CHURN_RADAR_COLUMNS} />;
          if (activeReport.id === 'retry_storm_history') {
            const rows = Array.isArray(reportData?.rows) ? reportData.rows : [];
            const d = reportData?.diagnostics;
            const thr = reportData?.thresholds;
            if (rows.length === 0 && d) {
              return (
                <div className="p-6 space-y-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                    <div className="font-semibold mb-2">No retry storms detected with current thresholds</div>
                    <ul className="list-disc list-inside text-xs space-y-1 text-amber-800">
                      <li>Vendor-day rows scanned: <strong>{formatNumber(d.raw_vendor_day_rows)}</strong> · distinct vendors: <strong>{formatNumber(d.distinct_vendors)}</strong></li>
                      <li>Vendors with ≥7 days of data: <strong>{formatNumber(d.vendors_with_enough_data)}</strong> (skipped {formatNumber(d.vendors_skipped_for_sparse_data)} for sparse data)</li>
                      <li>Peak observed retry rate: <strong>{d.max_observed_rate_pct}%</strong> · peak retries in a day: <strong>{formatNumber(d.max_observed_retries_in_day)}</strong></li>
                      {thr && (
                        <li>Current thresholds — multiplier: <strong>{thr.multiplier}×</strong> · min retries/day: <strong>{thr.min_retries}</strong> · min volume/day: <strong>{thr.min_volume}</strong></li>
                      )}
                    </ul>
                    <div className="mt-3 text-xs">
                      Try lowering <em>min retries/day</em> toward {Math.max(1, Math.floor((d.max_observed_retries_in_day || 0) / 4))} or <em>min volume/day</em> to see entries, then use the multiplier to filter noise.
                    </div>
                  </div>
                </div>
              );
            }
            return <TableView report={activeReport} data={reportData} columns={RETRY_STORM_COLUMNS} />;
          }
          return <div className="p-6 text-gray-500">No column schema for {activeReport.id}</div>;
        case 'grid':       return <GridView report={activeReport} data={reportData} />;
        case 'scorecard':  return <ScorecardView report={activeReport} data={reportData} />;
        case 'timeseries': return <TimeSeriesView report={activeReport} data={reportData} onVendorChange={(vid) => setParam('vendor_id', vid)} />;
        case 'heatmap':    return (
          <HeatmapView
            report={activeReport}
            data={reportData}
            params={reportParams}
            onCustomerChange={(cid) => setParam('customer_id', cid)}
            onModeChange={(m) => setParam('fingerprint_mode', m)}
            onTopNChange={(n) => setParam('fingerprint_top_n', n)}
            onCustomerIdsChange={(ids) => setParam('fingerprint_customer_ids', ids)}
            onNormChange={(norm) => setParam('fingerprint_norm', norm)}
          />
        );
        case 'concentration': return <ConcentrationView report={activeReport} data={reportData} />;
        case 'customer_health': return <CustomerHealthView report={activeReport} data={reportData} />;
        case 'sla': return <SlaView report={activeReport} data={reportData} />;
        case 'decomposition': return <DecompositionView report={activeReport} data={reportData} />;
        case 'dual_section':
          if (activeReport.id === 'expansion_opportunities') return (
            <DualSectionView
              report={activeReport}
              data={reportData}
              sections={[
                {
                  key: 'new_countries',
                  title: 'New Countries/Services',
                  description: 'Customer-country pairs active now but not in baseline window — upsell opportunities on genuinely new routes.',
                  columns: EXPANSION_NEW_COUNTRIES_COLUMNS,
                },
                {
                  key: 'growth',
                  title: 'Volume Growth',
                  description: 'Customers with significant volume growth vs baseline.',
                  columns: EXPANSION_GROWTH_COLUMNS,
                },
              ]}
            />
          );
          return <div className="p-6 text-gray-500">No dual-section config for {activeReport.id}</div>;
        default:           return <div className="p-6 text-gray-500">Unknown view type: {activeReport.view_type}</div>;
      }
    };

    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex justify-between items-center">
            <div className="flex items-center space-x-3 min-w-0">
              <button
                onClick={() => { setActiveReport(null); setReportData(null); setReportError(null); }}
                className="p-1.5 rounded-lg hover:bg-white/60 transition-colors flex-shrink-0"
              >
                <ArrowLeft size={18} className="text-gray-600" />
              </button>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 flex items-center space-x-2 truncate">
                  {React.createElement(resolveIcon(activeReport.icon), { size: 18, className: 'text-blue-600 flex-shrink-0' })}
                  <span className="truncate">{activeReport.name}</span>
                </h2>
                <p className="text-xs text-gray-500 truncate">{activeReport.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <div className="flex items-center space-x-1 text-xs" title="Defaults to the latest date with data. Pick another date to run a snapshot at that point in time.">
                <span className="text-gray-400">As of</span>
                <input
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                  className="border border-gray-300 rounded px-1.5 py-1 text-xs"
                  placeholder="auto"
                />
              </div>
              <button
                onClick={() => executeReport(activeReport)}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5 text-sm font-medium"
              >
                <RefreshCw size={13} className={reportLoading ? 'animate-spin' : ''} />
                <span>{reportLoading ? 'Running…' : 'Run'}</span>
              </button>
              {reportData && (reportData.rows || reportData.cells) && (
                <button
                  onClick={exportCSV}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1 text-xs text-gray-600"
                >
                  <Download size={13} /><span>CSV</span>
                </button>
              )}
            </div>
          </div>

          {/* Report-specific tuning controls */}
          {(activeReport.id === 'customer_health' ||
            activeReport.id === 'corridor_radar' ||
            activeReport.id === 'margin_leakage_intel' ||
            activeReport.id === 'executive_scorecard' ||
            activeReport.id === 'vendor_reliability' ||
            activeReport.id === 'latency_decomposition' ||
            activeReport.id === 'error_code_drift' ||
            activeReport.id === 'churn_radar' ||
            activeReport.id === 'expansion_opportunities' ||
            activeReport.id === 'traffic_fingerprint' ||
            activeReport.id === 'profitability_by_segment' ||
            activeReport.id === 'vendor_cost_trend' ||
            activeReport.id === 'concentration_risk' ||
            activeReport.id === 'customer_self_service' ||
            activeReport.id === 'sla_vs_reality' ||
            activeReport.id === 'seasonality_decomposition' ||
            activeReport.id === 'retry_storm_history') && (
            <div className="px-6 py-2 border-b bg-white flex items-center gap-4 text-xs flex-wrap">
              {activeReport.id === 'customer_health' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min volume</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.min_volume}
                      onChange={(e) => setParam('min_volume', e.target.value)}
                      placeholder="1"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                </>
              )}

              {activeReport.id === 'corridor_radar' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min wkly vol</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.min_weekly_volume}
                      onChange={(e) => setParam('min_weekly_volume', e.target.value)}
                      placeholder="1"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min weeks</span>
                    <input
                      type="number"
                      min="2"
                      value={reportParams.min_weeks}
                      onChange={(e) => setParam('min_weeks', e.target.value)}
                      placeholder="2"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-16"
                    />
                  </label>
                </>
              )}

              {activeReport.id === 'margin_leakage_intel' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="30"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min volume</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.min_volume}
                      onChange={(e) => setParam('min_volume', e.target.value)}
                      placeholder="1"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportParams.only_negative}
                      onChange={(e) => setParam('only_negative', e.target.checked)}
                      className="w-3 h-3"
                    />
                    <span className="text-gray-700">Only negative margin</span>
                  </label>
                </>
              )}

              {activeReport.id === 'executive_scorecard' && (
                <label className="flex items-center gap-1.5">
                  <span className="text-gray-400 uppercase tracking-wider">Window</span>
                  <select
                    value={reportParams.window_weeks}
                    onChange={(e) => setParam('window_weeks', parseInt(e.target.value, 10))}
                    className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                  >
                    <option value={1}>1 week (WoW/MoM/YoY)</option>
                    <option value={4}>4 weeks</option>
                    <option value={12}>12 weeks</option>
                  </select>
                </label>
              )}

              {activeReport.id === 'vendor_reliability' && (
                <label className="flex items-center gap-1.5">
                  <span className="text-gray-400 uppercase tracking-wider">Min volume</span>
                  <input
                    type="number"
                    min="0"
                    value={reportParams.min_volume}
                    onChange={(e) => setParam('min_volume', e.target.value)}
                    placeholder="1"
                    className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                  />
                </label>
              )}

              {activeReport.id === 'latency_decomposition' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="2"
                      max="365"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="30"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Granularity</span>
                    <select
                      value={reportParams.granularity}
                      onChange={(e) => setParam('granularity', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                    </select>
                  </label>
                  <span className="text-gray-400 italic">
                    Vendor filter: use picker inside chart
                  </span>
                </>
              )}

              {activeReport.id === 'error_code_drift' && (
                <label className="flex items-center gap-1.5">
                  <span className="text-gray-400 uppercase tracking-wider">Min error count</span>
                  <input
                    type="number"
                    min="0"
                    value={reportParams.min_error_count}
                    onChange={(e) => setParam('min_error_count', e.target.value)}
                    placeholder="1"
                    className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                  />
                </label>
              )}

              {activeReport.id === 'churn_radar' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min baseline volume</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.min_baseline_volume}
                      onChange={(e) => setParam('min_baseline_volume', e.target.value)}
                      placeholder="10"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min drop %</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="5"
                      value={reportParams.min_drop_pct}
                      onChange={(e) => setParam('min_drop_pct', e.target.value)}
                      placeholder="0"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <span className="text-gray-400 italic text-[11px]">
                    0 = all shrinking customers; 20 = only ≥20% drop
                  </span>
                </>
              )}

              {activeReport.id === 'expansion_opportunities' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">New-country min volume</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.min_new_country_volume}
                      onChange={(e) => setParam('min_new_country_volume', e.target.value)}
                      placeholder="10"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Growth min volume</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.min_growth_volume}
                      onChange={(e) => setParam('min_growth_volume', e.target.value)}
                      placeholder="10"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Growth min %</span>
                    <input
                      type="number"
                      min="0"
                      step="5"
                      value={reportParams.min_growth_pct}
                      onChange={(e) => setParam('min_growth_pct', e.target.value)}
                      placeholder="20"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                </>
              )}

              {activeReport.id === 'traffic_fingerprint' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="2"
                      max="365"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="30"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  {reportParams.fingerprint_mode === 'compare' ? (
                    <label className="flex items-center gap-1.5">
                      <span className="text-gray-400 uppercase tracking-wider">Top N (when no custom selection)</span>
                      <input
                        type="number"
                        min="1"
                        max="9"
                        value={reportParams.fingerprint_top_n}
                        onChange={(e) => setParam('fingerprint_top_n', parseInt(e.target.value, 10) || 6)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs w-16"
                      />
                    </label>
                  ) : (
                    <span className="text-gray-400 italic text-[11px]">
                      Pick a customer inside the heatmap · switch to Compare mode to see multiple
                    </span>
                  )}
                </>
              )}

              {activeReport.id === 'profitability_by_segment' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="2"
                      max="365"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="30"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Granularity</span>
                    <select
                      value={reportParams.granularity}
                      onChange={(e) => setParam('granularity', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Segment</span>
                    <select
                      value={reportParams.segment}
                      onChange={(e) => setParam('segment', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value="all">All (aggregate)</option>
                      <option value="am">Account Manager</option>
                      <option value="country">Country</option>
                      <option value="customer">Customer</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Top N</span>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={reportParams.top_n}
                      onChange={(e) => setParam('top_n', parseInt(e.target.value, 10) || 5)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-16"
                    />
                  </label>
                </>
              )}

              {activeReport.id === 'vendor_cost_trend' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="7"
                      max="365"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="60"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Granularity</span>
                    <select
                      value={reportParams.granularity}
                      onChange={(e) => setParam('granularity', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                    </select>
                  </label>
                  <span className="text-gray-400 italic text-[11px]">
                    Vendor picker is inside the chart
                  </span>
                </>
              )}

              {activeReport.id === 'concentration_risk' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="2"
                      max="365"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="30"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Top N</span>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={reportParams.top_n}
                      onChange={(e) => setParam('top_n', parseInt(e.target.value, 10) || 10)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-16"
                    />
                  </label>
                </>
              )}

              {activeReport.id === 'customer_self_service' && (
                <label className="flex items-center gap-1.5">
                  <span className="text-gray-400 uppercase tracking-wider">Customer</span>
                  <CustomerPicker
                    value={reportParams.ss_customer_id}
                    onChange={(id) => setParam('ss_customer_id', id)}
                    asOf={asOf}
                    placeholder="Select customer…"
                  />
                  <span className="text-gray-400 italic text-[11px]">
                    {scopedCustomerId
                      ? 'Scoped to your account by login'
                      : 'Required for internal users'}
                  </span>
                </label>
              )}

              {activeReport.id === 'sla_vs_reality' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Customer</span>
                    <CustomerPicker
                      value={reportParams.ss_customer_id}
                      onChange={(id) => setParam('ss_customer_id', id)}
                      asOf={asOf}
                      placeholder="Select customer…"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="2"
                      max="365"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="30"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Granularity</span>
                    <select
                      value={reportParams.granularity}
                      onChange={(e) => setParam('granularity', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Delivery ≥</span>
                    <input
                      type="number"
                      min="50"
                      max="100"
                      step="0.5"
                      value={reportParams.sla_delivery_rate}
                      onChange={(e) => setParam('sla_delivery_rate', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-16"
                    />
                    <span className="text-gray-500 text-xs">%</span>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">E2E ≤</span>
                    <input
                      type="number"
                      min="100"
                      max="600000"
                      step="500"
                      value={reportParams.sla_e2e_ms}
                      onChange={(e) => setParam('sla_e2e_ms', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-24"
                    />
                    <span className="text-gray-500 text-xs">ms</span>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Error ≤</span>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="0.1"
                      value={reportParams.sla_error_rate}
                      onChange={(e) => setParam('sla_error_rate', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-16"
                    />
                    <span className="text-gray-500 text-xs">%</span>
                  </label>
                </>
              )}

              {activeReport.id === 'seasonality_decomposition' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="28"
                      max="730"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="90"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Metric</span>
                    <select
                      value={reportParams.decomp_metric}
                      onChange={(e) => setParam('decomp_metric', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      <option value="volume">Volume</option>
                      <option value="revenue">Revenue</option>
                      <option value="delivery_rate">Delivery Rate</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Customer</span>
                    <CustomerPicker
                      value={reportParams.decomp_customer_id}
                      onChange={(id) => setParam('decomp_customer_id', id)}
                      asOf={asOf}
                      allowBlank
                      blankLabel="All customers (global)"
                      placeholder="All customers (global)"
                    />
                  </label>
                  <span className="text-gray-400 italic text-[11px]">
                    Min 28 days for meaningful decomposition
                  </span>
                </>
              )}

              {activeReport.id === 'retry_storm_history' && (
                <>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Window (days)</span>
                    <input
                      type="number"
                      min="14"
                      max="730"
                      value={reportParams.window_days}
                      onChange={(e) => setParam('window_days', e.target.value)}
                      placeholder="90"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Multiplier (×)</span>
                    <input
                      type="number"
                      min="1.5"
                      max="20"
                      step="0.5"
                      value={reportParams.storm_multiplier}
                      onChange={(e) => setParam('storm_multiplier', e.target.value)}
                      placeholder="2"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-16"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min retries/day</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.storm_min_retries}
                      onChange={(e) => setParam('storm_min_retries', e.target.value)}
                      placeholder="10"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-gray-400 uppercase tracking-wider">Min volume/day</span>
                    <input
                      type="number"
                      min="0"
                      value={reportParams.storm_min_volume}
                      onChange={(e) => setParam('storm_min_volume', e.target.value)}
                      placeholder="100"
                      className="border border-gray-300 rounded px-2 py-1 text-xs w-20"
                    />
                  </label>
                  <span className="text-gray-400 italic text-[11px]">
                    Storm = retry rate ≥ multiplier × vendor baseline
                  </span>
                </>
              )}

              <span className="text-gray-400 italic ml-auto">
                Changes apply on next Run
              </span>
            </div>
          )}

          {reportData?.as_of && (
            <div className="px-6 py-2 bg-gray-50 border-b text-xs text-gray-600 flex items-center space-x-4 overflow-x-auto">
              <span>As of: <strong>{reportData.as_of}</strong></span>
              {reportData.current_window && (
                <span>
                  Current: <strong>{reportData.current_window[0]}</strong> → <strong>{reportData.current_window[1]}</strong>
                  {reportData.window_weeks && reportData.window_weeks > 1 && (
                    <span className="text-gray-400 ml-1">({reportData.window_weeks}w)</span>
                  )}
                </span>
              )}
              {reportData.baseline_window && (
                <span>Baseline: <strong>{reportData.baseline_window[0]}</strong> → <strong>{reportData.baseline_window[1]}</strong></span>
              )}
              {reportData.window && (
                <span>Window: <strong>{reportData.window[0]}</strong> → <strong>{reportData.window[1]}</strong></span>
              )}
              {reportData.total_dollars_at_risk != null && (
                <span className="ml-auto text-red-600 font-semibold">
                  Total $ at risk: {formatCurrency(reportData.total_dollars_at_risk)}
                </span>
              )}
              {reportData.total_count != null && (
                <span className="ml-auto">Rows: <strong>{reportData.total_count}</strong></span>
              )}
            </div>
          )}

          {renderBody()}
        </div>
      </div>
    );
  }

  // ─── Catalog view ──────────────────────────────────────────
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center justify-between">
          <div className="flex space-x-1 overflow-x-auto">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.key;
              const count = cat.key === 'all'
                ? catalog.length
                : catalog.filter((r) => r.category === cat.key).length;
              // Hide empty subcategories (after backend has filtered the catalog
              // for the current user). Keep the 'all' tab even if empty so the
              // user has a way back to the root view.
              if (cat.key !== 'all' && count === 0) return null;
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={14} />
                  <span>{cat.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative ml-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search reports…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading reports…</span>
        </div>
      ) : filteredCatalog.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Search size={48} className="mx-auto mb-3 opacity-40" />
          <p>No reports match your search</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCatalog.map((r) => {
            const Icon = resolveIcon(r.icon);
            return (
              <button
                key={r.id}
                onClick={() => executeReport(r)}
                className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:shadow-md hover:border-blue-300 transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                    <Icon size={20} className="text-blue-600" />
                  </div>
                  <ChevronRight size={18} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{r.name}</h3>
                <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{r.description}</p>
                {r.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {r.tags.slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IntelligenceLibrary;
